import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { NetworkStatus } from "apollo-client";
import produce from "immer";
import { uniqBy } from "lodash";
import moment from "moment";
import momentTz from "moment-timezone";
import ContentLoader from "react-content-loader";
import { useInView } from "react-intersection-observer";
import useResizeObserver from "use-resize-observer/polyfilled";
import { ChevronDown } from "@sonder/ui/source/icons";
import { DateTimePicker } from "@sonder/ui/source/styled/components/DateTimePicker";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { DropdownContent } from "@sonder/ui/source/styled/components/DropdownContent";
import { TimeRange } from "@sonder/ui/source/styled/components/TimeRange";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import { InputGroup } from "@sonder/ui/source/styled/fragments/InputGroup";
import {
  SchedulerLayout,
  SchedulerRow,
} from "@sonder/ui/source/styled/layouts/SchedulerLayout";
import { colors, sizes, zIndex } from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { useStore as useAuthStore } from "auth/store";
import { EmptyContent } from "core/components/EmptyContent";
import { Layout } from "core/components/Layout";
import { ONE_MINUTE } from "core/constants";
import { useCurrentUserTimezone } from "core/hooks/useCurrentTimeZone";
import { usePageVisibilityPolling } from "core/hooks/usePageVisibilityPolling";
import {
  GetTaskSchedulesResponse,
  useRefetchableGetTaskSchedulesQuery,
} from "core/queries/GetTaskSchedules";
import {
  closeItem,
  setDisabled,
  setSelecting,
  setUnscheduledTask,
  updateDateRange,
} from "schedule/actions";
import { AutoScheduleModal } from "schedule/components/AutoScheduleModal";
import { BuildingList } from "schedule/components/BuildingList";
import { BulkActionsHeader } from "schedule/components/BulkActionsHeader";
import { CleanTypeFilters } from "schedule/components/Filters/CleanTypeFilters";
import { CleaningCompanyFilters } from "schedule/components/Filters/CleaningCompanyFilters";
import { ClearFilterButton } from "schedule/components/Filters/ClearFilterButton";
import { SameDayCleansFilterButton } from "schedule/components/Filters/SameDayCleansFilterButton";
import { ScheduleStatusFilters } from "schedule/components/Filters/ScheduleStatusFilters";
import { QuickView } from "schedule/components/QuickView";
import { ScheduleHeader } from "schedule/components/ScheduleHeader";
import { TaskStatusFilters } from "schedule/components/TaskStatusFilters";
import { TopLevelMetrics } from "schedule/components/TopLevelMetrics";
import { getTodayDateRange, getViewItemId } from "schedule/reducer";
import { useStore } from "schedule/store";
import { AssigneeFilters } from "tasks/components/Filters";
import { BuildingFilters } from "tasks/components/Filters/BuildingFilters";
import {
  TaskListOuterContainer,
  TaskListOuterContainerProps,
} from "tasks/components/TaskList/TaskList";
import { useStore as useTasksStore } from "tasks/store";
import { TypeEnum } from "typings/globalGQLTypes";
import { UnscheduleTaskModal } from "./UnscheduleTaskModal";

const StickyHeader = styled.div`
  background: linear-gradient(
    180deg,
    ${colors.grayF} 0%,
    ${colors.grayF} 75%,
    rgba(250, 250, 250, 0) 100%
  );
  min-width: 100%;
  padding-bottom: 1.5rem;
  position: sticky;
  top: 0;
  z-index: ${zIndex.schedulerHeader};
`;

const SchedulerRowWrapper = styled(SchedulerRow)`
  position: relative;
  min-height: auto;
`;

const TodayButton = styled(Button)`
  margin-right: 1rem;
`;

const AutoScheduleButton = styled(Button)`
  margin-left: auto;
`;

const ScheduleViewOuterContainer = styled(TaskListOuterContainer)`
  max-width: ${sizes.schedulerMaxWidth};
  min-width: ${sizes.listWidth};
`;

export const TimeRangeOuterContainer = styled(TaskListOuterContainer)`
  align-items: center;
  background: ${colors.grayF};
  box-sizing: border-box;
  display: flex;
  max-width: none;
  padding-right: 1.5rem;
`;

const EmptyStateContainer = styled.div`
  color: ${colors.gray6};
  text-align: center;
`;

const SelectionButton = styled(Button)`
  color: ${colors.blue7};
  display: inline-flex;
  flex: 0 1 auto;
  margin-right: 1rem;
`;

const StyledIntersectionObserver = styled.div`
  padding: 1px;
`;

interface BuildingListOuterContainerProps extends TaskListOuterContainerProps {
  isDisabled: boolean;
}

const BuildingListOuterContainer = styled(
  ScheduleViewOuterContainer,
)<BuildingListOuterContainerProps>`
  flex: 1 1 auto;
  opacity: ${props => (props.isDisabled ? 0.5 : 1)};
  padding-top: 0;
  position: relative;
`;

const StyledScheduleViewContentLoader = styled(ContentLoader)`
  height: 300px;
  width: 126px;
`;

export const ScheduleViewContentLoader = () => (
  <StyledScheduleViewContentLoader height={300} width={126}>
    <rect fill="#000" height="13" width="106" x="-2" y="13" />
    <rect fill="#000" height="13" width="82" x="-2" y="60" />
    <rect fill="#000" height="13" width="100" x="-2" y="107" />
    <rect fill="#000" height="13" width="50" x="-2" y="159" />
    <rect fill="#000" height="13" width="34" x="-2" y="206" />
    <rect fill="#000" height="13" width="82" x="-3" y="260" />
  </StyledScheduleViewContentLoader>
);

const DateTimePickerContainer = styled.div`
  display: flex;
`;

const onFetchComplete = (
  prev: GetTaskSchedulesResponse,
  fetchMoreResult: GetTaskSchedulesResponse,
) => {
  const prevTasks = prev?.taskSchedulerConnections?.places || [];
  const newTasks = fetchMoreResult?.taskSchedulerConnections?.places || [];

  return produce(fetchMoreResult, draft => {
    draft.taskSchedulerConnections.places = [...prevTasks, ...newTasks];
    return draft;
  });
};

export const createTrigger = (
  date: moment.Moment | null,
  disabled: boolean,
) => (
  <Button iconRight disabled={disabled}>
    {date && date.format("ddd MMM D, YYYY")}
    <ChevronDown />
  </Button>
);

export const ScheduleViewContent = () => {
  const { track } = useSegment();
  const { ref: buildingsListRef, height: buildingsListHeight } =
    useResizeObserver();
  const [footerRef, isFooterInView] = useInView();
  const { state: authState } = useAuthStore();
  const {
    state: {
      filters: { assignee, buildingIds },
    },
  } = useTasksStore();
  const [isAutoscheduleModalOpen, setIsAutoScheduleModalOpen] = useState(false);
  const cityId = authState?.user?.cityId;
  const timezone = useCurrentUserTimezone();
  const {
    state: {
      dateRange,
      isSelecting,
      viewedItems,
      unscheduledTaskId,
      filters: {
        cleaningCompanyIds,
        cleaningRequestCategory,
        sameDayCleansOnly,
        scheduleStatus,
        status,
      },
    },
    dispatch,
  } = useStore();
  const isAutoschedulerFeatureEnabled = authState?.user?.featureFlags?.includes(
    "dispatch_auto_scheduler_enabled",
  );
  const endAt = dateRange.endAt.toISOString();
  const startAt = dateRange.startAt.toISOString();

  const todayDate = momentTz.tz(timezone);
  const todayDateRange = getTodayDateRange(todayDate);
  const taskFilter = {
    assignee,
    dueOrLatestEndAt: endAt,
    scheduledOrEarliestStartAt: startAt,
    status,
  };

  const variables = {
    cursor: "0",
    endAt: taskFilter.dueOrLatestEndAt,
    limit: 500,
    startAt: taskFilter.scheduledOrEarliestStartAt,
    filter: {
      ...taskFilter,
      buildingIds: buildingIds || [],
      city: cityId as string,
      cleaningCompanyIds,
      cleaningRequestCategory,
      sameDayCleansOnly,
      scheduleStatus,
      taskType: [TypeEnum.Tom_Tasks_Clean, TypeEnum.Tom_Tasks_SpaceClean],
    },
  };

  const {
    data,
    fetchMore,
    loading,
    networkStatus,
    refetch,
    startPolling,
    stopPolling,
  } = useRefetchableGetTaskSchedulesQuery({
    pollInterval: ONE_MINUTE,
    skip: !cityId,
    variables,
  });
  usePageVisibilityPolling({
    pollInterval: ONE_MINUTE,
    refetch,
    variables,
    startPolling,
    stopPolling,
  });

  const cursor = data?.taskSchedulerConnections?.cursor;
  const hasMore = data?.taskSchedulerConnections?.hasMore;
  const isPolling = networkStatus === NetworkStatus.poll;
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;
  const isRefetching = networkStatus === NetworkStatus.refetch;
  const isAnyLoading = loading && !isRefetching && !isPolling;

  const onFetchMore = () => {
    fetchMore({
      updateQuery: (prev, { fetchMoreResult }) =>
        fetchMoreResult
          ? onFetchComplete(
              prev as GetTaskSchedulesResponse,
              fetchMoreResult as GetTaskSchedulesResponse,
            )
          : prev,
      variables: {
        cursor,
      },
    });
  };
  useEffect(() => {
    if (hasMore && isFooterInView && !isFetchingMore && !isAnyLoading) {
      onFetchMore();
    }
  }, [hasMore, isFooterInView, isFetchingMore, isAnyLoading, onFetchMore]);
  const shouldShowLoader = isFetchingMore || isAnyLoading;
  const isDisabled = isAnyLoading && !isFetchingMore && !!data;
  useEffect(() => {
    dispatch(setDisabled(isDisabled));
  }, [isDisabled]);

  useEffect(() => {
    dispatch(updateDateRange(todayDateRange));
  }, [timezone]);

  const places = data?.taskSchedulerConnections?.places || [];
  const buildings = uniqBy(
    places.map(place => place.building),
    "id",
  );
  const hasNoResults =
    !shouldShowLoader && !hasMore && places.length === 0 && cityId;

  const isToday = todayDate
    .clone()
    .startOf("day")
    .isSame(dateRange.startAt.clone().startOf("day"));
  const differenceInDays = dateRange.startAt
    .clone()
    .startOf("day")
    .diff(todayDate.clone().startOf("day"), "days");
  const canAutoschedule =
    !loading &&
    !hasNoResults &&
    ((isToday && todayDate.hour() < 16) ||
      (differenceInDays > 0 && differenceInDays < 7));

  return (
    <Layout
      pageSubheader={
        <>
          {isSelecting ? (
            <BulkActionsHeader />
          ) : (
            <ScheduleHeader>
              <SelectionButton
                noBorder
                onClick={() => dispatch(setSelecting(true))}
              >
                Select Mode
              </SelectionButton>
              <InputGroup>
                <SameDayCleansFilterButton />
                <AssigneeFilters shouldLoadCleaningCompanyEmployees />
                <BuildingFilters />
                <CleaningCompanyFilters />
                <TaskStatusFilters />
                <CleanTypeFilters />
                <ClearFilterButton />
              </InputGroup>
              <ScheduleStatusFilters />
            </ScheduleHeader>
          )}
          <TimeRangeOuterContainer isCollapsed={false}>
            <TodayButton
              disabled={isSelecting}
              onClick={() => {
                track({
                  eventName: "schedule_view",
                  action: "click",
                  element: "today_filter",
                  props: {
                    endAt: todayDateRange.endAt.toISOString(),
                    startAt: todayDateRange.startAt.toISOString(),
                  },
                });
                dispatch(updateDateRange(todayDateRange));
              }}
            >
              Today
            </TodayButton>
            <DateTimePickerContainer>
              <Dropdown
                trigger={createTrigger(
                  dateRange.startAt,
                  isDisabled || isSelecting,
                )}
              >
                {({ isOpen }) =>
                  isOpen && (
                    <DropdownContent>
                      <DateTimePicker
                        date={dateRange.startAt}
                        isOutsideRange={() => false}
                        onDateChange={nextDate => {
                          if (nextDate) {
                            const nextDateRange = getTodayDateRange(nextDate);
                            track({
                              eventName: "schedule_view",
                              action: "click",
                              element: "date_range_filter",
                              props: {
                                endAt: nextDateRange.endAt.toISOString(),
                                startAt: nextDateRange.startAt.toISOString(),
                              },
                            });
                            dispatch(updateDateRange(nextDateRange));
                          }
                        }}
                      />
                    </DropdownContent>
                  )
                }
              </Dropdown>
            </DateTimePickerContainer>
            {isAutoschedulerFeatureEnabled && (
              <>
                <AutoScheduleButton
                  disabled={!canAutoschedule}
                  onClick={() => {
                    setIsAutoScheduleModalOpen(true);
                    track({
                      eventName: "schedule_view",
                      action: "click",
                      element: "auto_schedule_button",
                      props: {},
                    });
                  }}
                >
                  Preschedule
                </AutoScheduleButton>
                <AutoScheduleModal
                  isOpen={isAutoscheduleModalOpen}
                  onClose={() => {
                    setIsAutoScheduleModalOpen(false);
                    track({
                      eventName: "schedule_view",
                      action: "click",
                      element: "auto_schedule_modal_close",
                      props: {},
                    });
                  }}
                />
              </>
            )}
          </TimeRangeOuterContainer>
        </>
      }
      pageTitle="Schedule"
    >
      <ScheduleViewOuterContainer isCollapsed={false}>
        <SchedulerLayout>
          <SchedulerRowWrapper>
            <TopLevelMetrics
              assignee={assignee}
              buildingIds={buildingIds}
              cityId={cityId}
              cleaningCompanyIds={cleaningCompanyIds}
              cleaningRequestCategory={cleaningRequestCategory}
              endAt={endAt}
              sameDayCleansOnly={sameDayCleansOnly}
              scheduleStatus={scheduleStatus}
              startAt={startAt}
              status={status}
            />
          </SchedulerRowWrapper>
        </SchedulerLayout>
      </ScheduleViewOuterContainer>

      <StickyHeader>
        {!hasNoResults && (
          <ScheduleViewOuterContainer isCollapsed={false}>
            <SchedulerLayout>
              <SchedulerRowWrapper>
                <TimeRange
                  date={todayDate}
                  endAt={dateRange.endAt}
                  height={buildingsListHeight || 0}
                  startAt={dateRange.startAt}
                  timezone={timezone}
                />
              </SchedulerRowWrapper>
            </SchedulerLayout>
          </ScheduleViewOuterContainer>
        )}
      </StickyHeader>
      <BuildingListOuterContainer
        isCollapsed={false}
        isDisabled={isDisabled}
        ref={buildingsListRef as React.RefObject<HTMLDivElement>}
      >
        {data && <BuildingList buildings={buildings} places={places} />}
        {shouldShowLoader && <ScheduleViewContentLoader />}
        {hasNoResults && (
          <EmptyStateContainer>
            There are no results for{" "}
            {[dateRange.startAt, dateRange.endAt]
              .map(d => d.format("MMM D, h:mm A"))
              .join(" - ")}
          </EmptyStateContainer>
        )}
        {data && hasMore && <StyledIntersectionObserver ref={footerRef} />}
      </BuildingListOuterContainer>
      {viewedItems &&
        viewedItems.map(viewedItem => (
          <QuickView
            isOpen
            key={getViewItemId(viewedItem)}
            onClose={() => {
              dispatch(closeItem([viewedItem]));
            }}
            {...(viewedItem || {})}
          />
        ))}
      <UnscheduleTaskModal
        isOpen={!!unscheduledTaskId}
        taskId={unscheduledTaskId}
        onClose={() => dispatch(setUnscheduledTask(""))}
      />
    </Layout>
  );
};

export const ScheduleView = () => {
  const { state: authState } = useAuthStore();
  const cityId = authState?.user?.cityId;
  const canViewScheduler =
    authState?.user?.isHKManager || authState?.user?.isCleaningManager;

  if (!cityId) {
    return (
      <Layout pageSubheader="" pageTitle="Schedule">
        <span />
      </Layout>
    );
  }

  if (!canViewScheduler) {
    return (
      <Layout pageSubheader="" pageTitle="Schedule">
        <EmptyContent>
          <p>
            Your account is not currently able to view the Schedule.
            <br />
            Please reach out to your manager, you will likely need Roles added
            to your account.
          </p>
        </EmptyContent>
      </Layout>
    );
  }

  return <ScheduleViewContent />;
};
